import {PearlHarborReelView} from "./PearlHarborReelView";
import {PearlHarborReelSet} from "../server/PearlHarborReelSet";
import {gsap, TimelineLite} from "gsap";
import {ISymbol, SimpleReelModelEventData, SimpleReelModelState} from "../../core/reel/IReelData";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {PearlHarborSymbol} from "./PearlHarborSymbol";
import {ISymbolData} from "../server/PearlHarborModel";

export class PearlHarborAvalancheReelModel {

    private _reelSetIndex: number = 0;
    private _spinning: boolean = false;
    private _masterSpinTimeline: TimelineLite | undefined;
    private _state: SimpleReelModelState = SimpleReelModelState.IDLE;
    private _reelId: number;
    private _reelView: PearlHarborReelView;
    private _reelSet: string[] | undefined;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     */
    public readonly events: APIEventSystem | undefined;

    constructor(id: number, triggerEvents: boolean = true) {
        this._reelId = id;
        this._reelView = new PearlHarborReelView(id);
        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data: SimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }
    }

    private setReelIndex(value: number): void {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    /**
     * Starting the actual spin by adding it to the masterSpinTimeline
     * @private
     */
    private startSpin() {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this._masterSpinTimeline = gsap.timeline();
        this._masterSpinTimeline.add(this.spin());
    }

    private spin(): TimelineLite {
        const timeline: gsap.core.Timeline = gsap.timeline();
        timeline.add(() => {this._reelView.getAllOutAnimation()});
        timeline.add(() => this.onSpinStartedCompleted());
        return timeline;
    }

    private onSpinStartedCompleted(): void {
        // todo in case anything needs to be done on spin start completed
    }

    private onReelStateChange(data: SimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    public init(symbols: ISymbolData[]): void {
        this._reelView.initReel(symbols);
    }

    public clear():void {
        this._reelView.clearReel();
    }

    /**
     * Adding bounce on reel spin start
     */
    public start(): TimelineLite {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(() => {this.state = SimpleReelModelState.START});
        tl.add(() => this.startSpin());

        return tl;
    }

    public stop(reelData: ISymbolData[]): TimelineLite {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(this._reelView.stop(reelData));
        tl.add(() => {
            this.state = SimpleReelModelState.STOPPED;
            this.state = SimpleReelModelState.IDLE;
        });
        return tl;
    }

    public getFillUpReelAnimation(): TimelineLite {
        return this.reelView.getFillUpReelAnimation();
    }

    /**
     * Calculates which symbols that should fall down and how many steps
     */
    public collapseReel(): TimelineLite {
        return this._reelView.collapseReel();
    }

    public getSymbol(position: number): ISymbol {
        return this._reelView.getSymbol(position);
    }

    public getAllSymbols(): ISymbol[] {
        return this._reelView.getAllSymbols();
    }

    public getSymbolByTargetPosition(rowId: number): PearlHarborSymbol | undefined {
        return this._reelView.getSymbolByTargetPosition(rowId);
    }

    set state(value: SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel: this.reelId, state: this._state});
        }
    }

    get reelId(): number {
        return this._reelId;
    }

    get reelView(): PearlHarborReelView {
        return this._reelView;
    }

    get bigSymbolView(): PIXI.Container {
        return this._reelView.bigSymbolView;
    }

    get specialSymbolView(): PIXI.Container {
        return this._reelView.specialSymbolContainer;
    }

    get reelSet(): string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.reelId}`);
        }
        return this._reelSet;
    }
}